import { PayloadAction, createSlice, current } from "@reduxjs/toolkit";
import { SortingDataType, TableSortDirection, sortArrayBy } from "../../common/logic/TableSortUtils";
import { setInitalPagination } from "./utils";
import { deleteSubCompanyThunk, editSubCompaniesThunk, fetchSubCompaniesThunk, fetchSubCompanyEmployeesThunk } from "../thunk/subCompaniesThunk";
import { CompanyType } from "../../types/companyManagementTypes";
import { EmployeeUserType } from "../../types/employeeType";

export interface SubCompaniesState {
  onSave: boolean;
  subCompaniesData: CompanyType[];
  editedSubCompaniesData: CompanyType[];
  subCompanyEmployees: Record<number, EmployeeUserType[]>;
}

const initialState: SubCompaniesState = {
  onSave: true,
  subCompaniesData: [],
  editedSubCompaniesData: [],
  subCompanyEmployees: {},
};

export const subCompaniesSlice = createSlice({
  name: "subCompanies",
  initialState,
  reducers: {
    setSubCompaniesData: (state, action: PayloadAction<CompanyType[]>) => {
      state.subCompaniesData = action.payload;
    },
    createSubCompany: (state, payload: PayloadAction<CompanyType>) => {
      state.subCompaniesData = [payload.payload, ...state.subCompaniesData];
    },
    sortSubCompaniesTable: (
      state,
      payload: PayloadAction<{
        dataField: string;
        direction: TableSortDirection;
        dataType: SortingDataType;
      }>
    ) => {
      const { dataField, dataType, direction } = payload.payload;
      const arrayToSort = current(state.subCompaniesData);
      state.subCompaniesData = sortArrayBy(arrayToSort, dataField, dataType, direction);
    },
    setEditedSubCompaniesData: (state, action: PayloadAction<CompanyType>) => {
      state.editedSubCompaniesData =
        state.editedSubCompaniesData.length === 0
          ? [action.payload]
          : [
            ...state.editedSubCompaniesData.filter((item) => item.id !== action.payload.id),
            action.payload,
          ];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSubCompaniesThunk.fulfilled, (state, action) => {
      state.subCompaniesData = action.payload as CompanyType[];
      setInitalPagination(state, action.payload as CompanyType[]);
    });
    builder.addCase(editSubCompaniesThunk.fulfilled, (state, action) => {
      state.subCompaniesData = state.subCompaniesData.map((item) =>
        item.id === action.payload.id ? action.payload : item
      );
      state.editedSubCompaniesData = [];
    });
    builder.addCase(deleteSubCompanyThunk.fulfilled, (state, action) => {
      state.subCompaniesData = state.subCompaniesData.filter(
        (item) => item.id !== action.payload
      );
    });
    builder.addCase(fetchSubCompanyEmployeesThunk.fulfilled, (state, action) => {
      const subCompanyId = action.meta.arg;
      state.subCompanyEmployees[subCompanyId] = action.payload;
    });
  },
});

export const {
  setSubCompaniesData,
  createSubCompany,
  sortSubCompaniesTable,
  setEditedSubCompaniesData,
} = subCompaniesSlice.actions;

export default subCompaniesSlice.reducer;
